import React from 'react';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  color?: 'blue' | 'green' | 'orange' | 'red';
}

const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
  color = 'blue',
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'w-10 h-6',
          toggle: 'h-5 w-5',
          translate: checked ? 'translate-x-4' : 'translate-x-0',
        };
      case 'lg':
        return {
          container: 'w-16 h-9',
          toggle: 'h-8 w-8',
          translate: checked ? 'translate-x-7' : 'translate-x-0',
        };
      default: // md
        return {
          container: 'w-14 h-8',
          toggle: 'h-7 w-7',
          translate: checked ? 'translate-x-6' : 'translate-x-0',
        };
    }
  };

  const getColorClasses = () => {
    if (!checked) {
      return 'bg-gray-200';
    }

    switch (color) {
      case 'green':
        return 'bg-green-500';
      case 'orange':
        return 'bg-orange-500';
      case 'red':
        return 'bg-red-500';
      default: // blue
        return 'bg-blue-600';
    }
  };

  const sizeClasses = getSizeClasses();
  const colorClasses = getColorClasses();

  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  return (
    <div className="flex items-center space-x-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={handleClick}
        className={`
          relative inline-flex items-center rounded-full transition-colors duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
          ${sizeClasses.container}
          ${colorClasses}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <span
          className={`
            inline-block transform rounded-full bg-white shadow-lg transition-transform duration-200 ease-in-out
            ${sizeClasses.toggle}
            ${sizeClasses.translate}
          `}
        />
      </button>

      {label && (
        <label
          onClick={!disabled ? handleClick : undefined}
          className={`
            text-sm font-medium text-gray-700
            ${!disabled ? 'cursor-pointer select-none' : 'cursor-not-allowed opacity-50'}
          `}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default Switch;

